import {
  Box,
  Button,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Text,
} from '@chakra-ui/react';
import {
  ArticleNavigationContext,
  ConceptNavigationContext,
  NavigationEntity,
} from '@bridge/navigationStore';
import { reactEvents } from '@bridge/reactEvents';
import { customersApi } from '@texas/api/endpoints/customersApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { Icons } from '@texas/components/shared/Icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ConceptBreadcrumbPickerProps {
  context:
    | ConceptNavigationContext
    | (ArticleNavigationContext & { concept: NavigationEntity });
  isCurrent?: boolean;
}

export function ConceptBreadcrumbPicker({
  context,
  isCurrent = false,
}: ConceptBreadcrumbPickerProps) {
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useApiResource(
    customersApi.getConcepts,
  );
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuListRef = useRef<HTMLDivElement>(null);
  const currentConceptRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    refetch(context.customer.id);
  }, [context.customer.id, refetch]);

  const concepts = useMemo(
    () =>
      (data ?? []).sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
      ),
    [data],
  );
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase();
  const filteredConcepts = useMemo(
    () =>
      normalizedSearchTerm
        ? concepts.filter((concept) =>
            concept.name.toLocaleLowerCase().includes(normalizedSearchTerm),
          )
        : concepts,
    [concepts, normalizedSearchTerm],
  );

  const openMenu = () => {
    setTimeout(() => {
      const menuList = menuListRef.current;
      const currentConcept = currentConceptRef.current;
      if (!menuList || !currentConcept) return;

      const menuRect = menuList.getBoundingClientRect();
      const conceptRect = currentConcept.getBoundingClientRect();
      const offset =
        currentConcept.offsetTop -
        menuRect.top -
        menuRect.height / 2 +
        conceptRect.height / 2;
      menuList.scrollTo({ top: offset });
    }, 50);
  };

  const closeMenu = () => {
    setSearchTerm('');
  };

  return (
    <Menu
      isLazy={true}
      placement="bottom-start"
      onClose={closeMenu}
      onOpen={openMenu}
    >
      <MenuButton
        as={Button}
        variant="unstyled"
        color={
          isCurrent ? 'navigation.breadcrumbCurrent' : 'navigation.contextMuted'
        }
        display="flex"
        h="auto"
        px={0}
        fontFamily="inherit"
        fontSize="inherit"
        fontWeight={isCurrent ? 'semibold' : 'normal'}
        lineHeight="inherit"
        rightIcon={<Icons.ChevronDown />}
        _hover={{ color: 'navigation.breadcrumbCurrent' }}
      >
        <Text
          flex={isCurrent ? 1 : undefined}
          minW={0}
          textAlign="left"
          noOfLines={1}
        >
          {context.concept.name}
        </Text>
      </MenuButton>
      <Portal>
        <MenuList
          ref={menuListRef}
          w="320px"
          maxW="calc(100vw - 32px)"
          maxH="360px"
          overflowY="auto"
        >
          <Box position="sticky" top={0} zIndex={1} p={2} bg="chakra-body-bg">
            <Input
              ref={searchInputRef}
              size="sm"
              variant="navigationBreadcrumb"
              value={searchTerm}
              placeholder={t('general.search')}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </Box>
          {loading && (
            <MenuItem isDisabled={true} icon={<Spinner size="xs" />}>
              {t('general.loading')}
            </MenuItem>
          )}
          {!loading && error && (
            <MenuItem isDisabled={true}>{t('general.error')}</MenuItem>
          )}
          {!loading && !error && filteredConcepts.length === 0 && (
            <MenuItem isDisabled={true}>{t('general.noData')}</MenuItem>
          )}
          {!loading &&
            !error &&
            filteredConcepts.map((concept) => {
              const isSelected = concept.id === context.concept.id;

              return (
                <MenuItem
                  ref={isSelected ? currentConceptRef : undefined}
                  key={concept.id}
                  bg={
                    isSelected
                      ? 'navigation.contextActiveBackground'
                      : undefined
                  }
                  fontWeight={isSelected ? 'bold' : 'normal'}
                  aria-current={isSelected ? 'page' : undefined}
                  onClick={() => {
                    reactEvents.routingChanged.dispatch({
                      to: 'concept.articles',
                      navObj: { conceptId: concept.id },
                    });
                  }}
                >
                  {concept.name}
                </MenuItem>
              );
            })}
        </MenuList>
      </Portal>
    </Menu>
  );
}
